import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlideOver } from '../../shared/SlideOver';
import { SelectableCard, SelectableRow } from '../../shared/SelectableControl';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { StudentData } from '../pages/StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { DOMAIN_META, getSubTestDefinition, DOMAIN_MODULE_MAP, isBegrijpendLezenFamily, isTaalverzorgingTaaFamily, type AssessmentDomainId } from '../../../assessment';
import { error as logError } from '../../../utils/logger';
import { genUuid } from '../../../utils/genUuid';
import { dataLayer } from '../../../data/dataLayer';
import { Clock, HelpCircle, Users, Calendar, CheckCircle2, Search, ChevronDown, ChevronRight, Check, CheckSquare, UserCheck, Layers } from 'lucide-react';
import { FAMILIES_BY_MODULE, type FamilyDefinition } from '../../../content';
import { DjayNarrator } from '../../../mascot';
import { X } from 'lucide-react';
interface CreateAssessmentPanelProps {
  open: boolean;
  onClose: () => void;
  students: StudentData[];
  selectedGroup: GroupId;
  teacherId: string;
  classroomId: string | null;
  onSuccess: () => void;
}
type Step = 1 | 2 | 3;
function getFamiliesForDomain(domainId: AssessmentDomainId): FamilyDefinition[] {
  const modules = DOMAIN_MODULE_MAP[domainId];
  let families = modules.flatMap(mod => FAMILIES_BY_MODULE.get(mod) || []);
  if (domainId === 'taalverzorging') {
    families = families.filter(f => f.module !== 'Taalbegrip' || isTaalverzorgingTaaFamily(f.familyId));
  } else if (domainId === 'begrijpend-lezen') {
    families = families.filter(f => f.module !== 'Taalbegrip' || isBegrijpendLezenFamily(f.familyId));
  }
  return families;
}
const STEP_CONFIG = [{
  num: 1,
  label: 'Vakgebied'
}, {
  num: 2,
  label: 'Onderdelen'
}, {
  num: 3,
  label: 'Klaarzetten'
}] as const;
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0
  })
};
export function CreateAssessmentPanel({
  open,
  onClose,
  students,
  selectedGroup,
  teacherId,
  classroomId,
  onSuccess
}: CreateAssessmentPanelProps) {
  const [step, setStep] = useState<Step>(1);
  const [direction, setDirection] = useState(1);
  const [selectedDomain, setSelectedDomain] = useState<AssessmentDomainId | null>(null);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [selectionMode, setSelectionMode] = useState<'whole' | 'specific' | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hideTip, setHideTip] = useState(false);
  useEffect(() => {
    const pref = localStorage.getItem('mp.teacher.hideAssessmentTip');
    if (pref === 'true') setHideTip(true);
  }, []);
  const dismissTip = () => {
    setHideTip(true);
    localStorage.setItem('mp.teacher.hideAssessmentTip', 'true');
  };
  useEffect(() => {
    if (open) {
      setStep(1);
      setDirection(1);
      setSelectedDomain(null);
      setSelectionMode(null);
      setSelectedStudentIds([]);
      setSelectedFamilyIds([]);
      setDeadline('');
      setSearchQuery('');
      setExpandedSkills({});
      setIsSubmitting(false);
      setShowSuccess(false);
      setSaveError(null);
    }
  }, [open]);
  useEffect(() => {
    if (selectedDomain) {
      setSelectedFamilyIds([]);
      setSearchQuery('');
      setExpandedSkills({});
    }
  }, [selectedDomain]);
  const handleNext = () => {
    if (step < 3) {
      setDirection(1);
      setStep(s => s + 1 as Step);
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(s => s - 1 as Step);
    }
  };
  const handleConfirm = async () => {
    if (!selectedDomain) return;
    setIsSubmitting(true);
    setSaveError(null);
    const domainDef = getSubTestDefinition(selectedDomain, selectedGroup);
    const questionCount = domainDef?.questionCount || 0;
    const estimatedMinutes = domainDef?.estimatedMinutes || 0;
    // uuid, geen genId: dit id landt in `assessment_assignments.id`, een
    // uuid-kolom. Zie src/utils/genUuid.ts.
    const assignmentId = genUuid();
    const allFamilies = getFamiliesForDomain(selectedDomain);
    const selectedFams = allFamilies.filter(f => selectedFamilyIds.includes(f.familyId));
    const uniqueSkills = Array.from(new Set(selectedFams.map(f => f.skill.replace(/_/g, ' '))));
    const skillSummary = uniqueSkills.join(', ');
    const wholeClass = selectionMode === 'whole';
    const assignment = {
      id: assignmentId,
      teacherId,
      classroomId,
      domainId: selectedDomain,
      domainLabel: DOMAIN_META[selectedDomain].label,
      group: selectedGroup,
      questionCount,
      estimatedMinutes,
      targetStudentIds: wholeClass ? [] : selectedStudentIds,
      wholeClass,
      selectedFamilyIds: selectedFamilyIds,
      type: 'targeted' as const,
      selectedSkillSummary: skillSummary,
      createdAt: Date.now(),
      deadline: deadline ? new Date(deadline).getTime() : null,
      status: 'active' as const
    };
    try {
      // De opslag geeft `false` terug als de opdracht niet bewaard is (quota
      // lokaal, RLS of een constraint op de server). Dat werd genegeerd, dus
      // de leerkracht kreeg "klaargezet!" te zien voor een toets die nergens
      // stond — en ontdekte dat pas als een leerling zei dat hij niets zag.
      const saved = await dataLayer.saveTeacherAssignment(assignmentId, assignment);
      if (!saved) {
        setSaveError('De toets kon niet worden klaargezet. Probeer het opnieuw; blijft het misgaan, ververs dan de pagina en controleer of je nog bent ingelogd.');
        return;
      }
      setShowSuccess(true);
    } catch (error) {
      logError('Failed to save assignment', error);
      setSaveError('De toets kon niet worden klaargezet door een onverwachte fout. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const domains: AssessmentDomainId[] = ['rekenen', 'taalverzorging', 'begrijpend-lezen'];
  const toggleStudent = (id: string) => {
    setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const isNextDisabled = () => {
    if (step === 1 && !selectedDomain) return true;
    if (step === 2 && selectedFamilyIds.length === 0) return true;
    if (step === 3 && selectionMode === null) return true;
    if (step === 3 && selectionMode === 'specific' && selectedStudentIds.length === 0) return true;
    return false;
  };
  // ── Step indicator ──────────────────────────────────────────────────────
  const renderStepIndicator = () => <div className="flex items-center gap-1 mb-4" data-id="element-9555" data-mp="3pso5oui497cd">
      {STEP_CONFIG.map((s, i) => {
      const isActive = step === s.num;
      const isDone = step > s.num;
      return <Fragment key={s.num} data-id="element-9556" data-mp="2x37ge5psm6kd" data-mpi={i}>
            {i !== 0 && <div className={`flex-1 h-[2px] rounded-full transition-colors duration-300 ${isDone ? 'bg-orange-500' : 'bg-slate-200'}`} data-id="element-9557" data-mp="2x4iim13lb69d" />}
            <div className="flex flex-col items-center gap-1" data-id="element-9558" data-mp="37151wlg9pp9a">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${isDone ? 'bg-orange-500 text-white' : isActive ? 'bg-orange-500 text-white ring-4 ring-orange-100' : 'bg-slate-100 text-slate-400'}`} data-id="element-9559" data-mp="36zk8x09lqedj">
              
                {isDone ? <Check className="w-3.5 h-3.5" data-id="element-9560" data-mp="1supj2qdzn5au" /> : s.num}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${isActive ? 'text-orange-600' : isDone ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-9561" data-mp="1xrg13j9csayw">
              
                {s.label}
              </span>
            </div>
          </Fragment>;
    })}
    </div>;

  // ── Running summary strip ───────────────────────────────────────────────
  const renderSummaryStrip = () => {
    const meta = selectedDomain ? DOMAIN_META[selectedDomain] : null;
    const Icon = meta?.icon;
    const hasDomain = !!selectedDomain;
    const hasContent = selectedFamilyIds.length > 0;
    const hasStudents = selectionMode !== null;
    if (!hasDomain && step === 1) return null;
    return <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} className="border-t border-slate-100 pt-3 pb-1" data-id="element-9562" data-mp="2ujjkkfltl8r9">
        
        <div className="flex items-center gap-3 flex-wrap" data-id="element-9563" data-mp="nuhviioetvp6">
          {hasDomain && meta && Icon && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9564" data-mp="iun5ehcgla56">
              <div className={`w-4 h-4 rounded flex items-center justify-center ${meta.bgColor} ${meta.color}`} data-id="element-9565" data-mp="eul0lzbfg0lo">
              
                <Icon className="w-2.5 h-2.5" data-id="element-9566" data-mp="9wb4nmhvovz0" />
              </div>
              <span className="text-xs font-semibold text-slate-700" data-id="element-9567" data-mp="16kzcjpzvr2up">
                {meta.shortLabel}
              </span>
            </div>}
          {hasContent && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9568" data-mp="12li4qpwzdtmd">
              <Layers className="w-3 h-3 text-slate-500" data-id="element-9569" data-mp="stknp2jak7fq" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9570" data-mp="st2llp9y6yzw">
                {selectedFamilyIds.length} onderdelen
              </span>
            </div>}
          {hasStudents && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9571" data-mp="1lh86cc104nzq">
              <UserCheck className="w-3 h-3 text-slate-500" data-id="element-9572" data-mp="1jm7zgvowi2ii" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9573" data-mp="1enoccrykmkok">
                {selectionMode === 'whole' ? `${students.length} leerlingen` : `${selectedStudentIds.length} leerling(en)`}
              </span>
            </div>}
          {deadline && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9574" data-mp="1on5fb1mfukgu">
              <Calendar className="w-3 h-3 text-slate-500" data-id="element-9575" data-mp="22ihcdfo2gn0x" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9576" data-mp="1xl7nq28i4fe5">
                {new Date(deadline).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short'
            })}
              </span>
            </div>}
        </div>
      </motion.div>;
  };
  // ── Step 1: Domain ──────────────────────────────────────────────────────
  const renderStep1 = () => <div className="flex flex-col h-full" data-id="element-9577" data-mp="2gd9veo360lc9">
      <div className="flex items-center gap-2.5 mb-4" data-id="element-9578" data-mp="2gadwoxjhtsmr">
        <p className="text-sm font-medium text-slate-600" data-id="element-9579" data-mp="2zz1x82m0iqmx">
          Kies het vakgebied voor{' '}
          <span className="font-bold text-slate-900" data-id="element-9580" data-mp="34w22wcey9rdi">
            groep {selectedGroup}
          </span>
        </p>
      </div>

      {!hideTip && <div className="flex items-start gap-3 p-3 mb-4 bg-orange-50/60 border border-orange-100 rounded-xl" data-id="element-9581" data-mp="2qzoo5b1sw42q">
          <DjayNarrator size="xs" emotion="pointing" data-id="element-9582" data-mp="2h7ixq5r75upn" />
          <div className="flex-1 text-xs text-slate-700 mt-0.5 leading-relaxed" data-id="element-9583" data-mp="2h6r430h5u720">
            <strong data-id="element-9584" data-mp="2lzaq8ltwyaif">Een vakgebied</strong> is een groot onderdeel zoals Rekenen
            of Taalverzorging. Kies waarover deze toets moet gaan.
          </div>
          <button onClick={dismissTip} aria-label="Sluit tip" className="text-slate-400 hover:text-slate-600 p-1 -mr-1 -mt-1" data-id="element-9585" data-mp="2c8ft2mf4pqi7">
        
            <X className="w-4 h-4" data-id="element-9586" data-mp="1y9ptn04u7eqo" />
          </button>
        </div>}

      <div className="space-y-2.5 flex-1" role="radiogroup" aria-label="Kies een vak voor deze toets" data-id="element-9587" data-mp="237hy0tmo4tbr">
      
        {domains.map(domainId => {
        const meta = DOMAIN_META[domainId];
        const def = getSubTestDefinition(domainId, selectedGroup);
        const isAvailable = def !== null && def.available;
        const isSelected = selectedDomain === domainId;
        return <SelectableCard key={domainId} selected={isSelected} disabled={!isAvailable} label={isAvailable ? meta.label : `${meta.label} — niet beschikbaar voor groep ${selectedGroup}`} onSelect={() => setSelectedDomain(domainId)} className={`p-3.5 rounded-xl border-2 transition-all ${!isAvailable ? 'opacity-40 border-slate-100 bg-slate-50 cursor-not-allowed' : isSelected ? 'border-orange-500 bg-orange-50/60 shadow-sm cursor-pointer' : 'border-slate-200 hover:border-orange-200 bg-white cursor-pointer'}`} data-id="element-9588" data-mp="3v4ri98hwuaqd">
            
              <div className="flex items-center gap-3.5" data-id="element-9589" data-mp="33foa3hmwkg8g">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.bgColor} ${meta.color}`} data-id="element-9590" data-mp="33h9518d4q16u">
                
                  <meta.icon className="w-5 h-5" data-id="element-9591" data-mp="1dzlr88uitzs0" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-9592" data-mp="1ixx30p4dmkcv">
                  <div className="flex items-center justify-between" data-id="element-9593" data-mp="1iyp064dmgq59">
                    <h3 className={`font-bold text-[15px] ${isSelected ? 'text-orange-900' : 'text-slate-900'}`} data-id="element-9594" data-mp="1myz2n727gyfd">
                    
                      {meta.label}
                    </h3>
                    {isSelected && <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0" data-id="element-9595" data-mp="1wq3kqfn6gzef" />}
                  </div>
                  <div className="flex items-center gap-3 mt-1" data-id="element-9596" data-mp="21nby82xdsf9c">
                    <span className="text-xs text-slate-500" data-id="element-9597" data-mp="26nrfh5ksa6wl">
                      {meta.description}
                    </span>
                  </div>
                  {isAvailable && def && <div className="flex items-center gap-3 mt-1.5 text-[11px] font-bold text-slate-400" data-id="element-9598" data-mp="2amqs7ccrb7yl">
                      <span className="flex items-center gap-1" data-id="element-9599" data-mp="gb286vro4f7c">
                        <HelpCircle className="w-3 h-3" data-id="element-9600" data-mp="gbkaoa4xbpnp" />
                        {def.questionCount} vr
                      </span>
                      <span className="flex items-center gap-1" data-id="element-9601" data-mp="3d4ohcgeuh1re">
                        <Clock className="w-3 h-3" data-id="element-9602" data-mp="3d450z44uelfh" />~{def.estimatedMinutes} min
                      </span>
                    </div>}
                  {!isAvailable && <span className="mt-1.5 text-[11px] font-bold text-rose-500 bg-rose-50 px-2 py-0.5 rounded inline-block" data-id="element-9603" data-mp="3m5md6bf4q2yw">
                      Niet beschikbaar
                    </span>}
                </div>
              </div>
            </SelectableCard>;
      })}
      </div>
    </div>;

  // ── Step 2: Family picker ───────────────────────────────────────────────
  const renderStep2 = () => {
    const allFamilies = selectedDomain ? getFamiliesForDomain(selectedDomain) : [];
    const filteredFamilies = allFamilies.filter(f => f.description.toLowerCase().includes(searchQuery.toLowerCase()) || f.skill.toLowerCase().includes(searchQuery.toLowerCase()));
    const grouped: Record<string, typeof allFamilies> = {};
    filteredFamilies.forEach(f => {
      if (!grouped[f.skill]) grouped[f.skill] = [];
      grouped[f.skill].push(f);
    });
    const toggleFamily = (id: string) => {
      setSelectedFamilyIds(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]);
    };
    const toggleSkillGroup = (familiesInSkill: FamilyDefinition[]) => {
      const ids = familiesInSkill.map(f => f.familyId);
      const allSelected = ids.every(id => selectedFamilyIds.includes(id));
      if (allSelected) {
        setSelectedFamilyIds(prev => prev.filter(id => !ids.includes(id)));
      } else {
        setSelectedFamilyIds(prev => Array.from(new Set([...prev, ...ids])));
      }
    };
    const toggleExpand = (skill: string) => {
      setExpandedSkills(prev => ({
        ...prev,
        [skill]: !prev[skill]
      }));
    };
    const handleSelectAll = () => {
      if (selectedFamilyIds.length === allFamilies.length) {
        setSelectedFamilyIds([]);
      } else {
        setSelectedFamilyIds(allFamilies.map(f => f.familyId));
      }
    };
    const selectedCount = selectedFamilyIds.length;
    const isAllSelected = allFamilies.length > 0 && selectedCount === allFamilies.length;
    return <div className="flex flex-col h-full" data-id="element-9604" data-mp="1lfxqfcddavsu">
        <div className="flex items-center gap-2.5 mb-3" data-id="element-9605" data-mp="1hfnb393oqt1b">
          <p className="text-sm font-medium text-slate-600" data-id="element-9606" data-mp="1hf5hylcbkuxm">
            Welke onderdelen wil je toetsen?
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-2" data-id="element-9607" data-mp="17hr48ths6k90">
          <div className="flex items-center gap-2" data-id="element-9608" data-mp="17jkaw8se94h0">
            <span className="text-sm font-bold text-orange-600" data-id="element-9609" data-mp="12jfrob03pvn3">
              {selectedCount}
            </span>
            <span className="text-xs text-slate-500" data-id="element-9610" data-mp="yi492pfnaaoe">
              / {allFamilies.length} geselecteerd
            </span>
          </div>
          <button onClick={handleSelectAll} className="flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700 px-2 py-1 rounded-lg hover:bg-orange-50 transition-colors" data-id="element-9611" data-mp="2wu2wqc11zhrc">
            
            <CheckSquare className="w-3.5 h-3.5" data-id="element-9612" data-mp="jm5rv6w8cqz3" />
            {isAllSelected ? 'Deselecteer' : 'Alles'}
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-3" data-id="element-9613" data-mp="ode2255dwgdb">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-9614" data-mp="se7t37e4ibaf" />
          <Input placeholder="Zoek..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-white h-10 text-sm" data-id="element-9615" data-mp="3rtkz3stanfd4" />
          
        </div>

        {/* Scrollable skill groups */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1.5 pb-2" data-id="element-9616" data-mp="4odhet5jisrr">
          {Object.entries(grouped).map(([skill, families]) => {
          const isExpanded = expandedSkills[skill];
          const familyIds = families.map(f => f.familyId);
          const selectedInSkill = familyIds.filter(id => selectedFamilyIds.includes(id)).length;
          const isAllSkillSelected = selectedInSkill === families.length;
          const isPartiallySelected = selectedInSkill > 0 && selectedInSkill < families.length;
          return <div key={skill} className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-id="element-9617" data-mp="rsjff4inmb8n">
                
                <div className="flex items-center justify-between min-h-[44px] px-3" data-id="element-9618" data-mp="15k686ude11mo">
                  <div className="flex items-center gap-2 cursor-pointer flex-1 py-2" onClick={() => toggleExpand(skill)} data-id="element-9619" data-mp="10qljxu1ljm0o">
                    
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-9620" data-mp="1aihit7bibg5y" /> : <ChevronRight className="w-4 h-4 text-slate-400" data-id="element-9621" data-mp="1ahpywtr9v6vi" />}
                    <span className="text-sm font-semibold text-slate-700 truncate" data-id="element-9622" data-mp="1ogpny2ip5ns7">
                      {skill.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${isAllSkillSelected ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-9623" data-mp="3q93z8iqqjtzn">
                      
                      {selectedInSkill}/{families.length}
                    </span>
                  </div>
                  <div className="cursor-pointer p-2 -mr-1" onClick={() => toggleSkillGroup(families)} data-id="element-9624" data-mp="3g5f76e0m36bk">
                    
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isAllSkillSelected ? 'bg-orange-500 border-orange-500 text-white' : isPartiallySelected ? 'bg-orange-100 border-orange-400 text-orange-500' : 'border-slate-300 bg-white'}`} data-id="element-9625" data-mp="32cz97rmkhyhi">
                      
                      {isAllSkillSelected && <Check className="w-3 h-3" data-id="element-9626" data-mp="2xcbcqqfkkpfw" />}
                      {isPartiallySelected && <div className="w-2 h-0.5 bg-current rounded-full" data-id="element-9627" data-mp="2jgr3kmpn7llo" />}
                    </div>
                  </div>
                </div>

                {isExpanded && <div className="border-t border-slate-100" data-id="element-9628" data-mp="hn9xvol2outk">
                    {families.map(f => {
                const isSelected = selectedFamilyIds.includes(f.familyId);
                return <SelectableRow key={f.familyId} selected={isSelected} onToggle={() => toggleFamily(f.familyId)} label={f.description} className="flex items-center gap-3 px-3 py-2.5 min-h-[44px] cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-50 last:border-b-0" data-id="element-9629" data-mp="1q72jyiijbr69">
                        
                          <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-9630" data-mp="1l6xzkheiacar">
                          
                            {isSelected && <Check className="w-3 h-3" data-id="element-9631" data-mp="1zwm19jqlncz0" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-9632" data-mp="1zyymq6vkg5gt">
                            <span className={`text-sm truncate block ${isSelected ? 'font-semibold text-slate-900' : 'text-slate-600'}`} data-id="element-9633" data-mp="23wwcpbx5uvl9">
                            
                              {f.description}
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-bold uppercase flex-shrink-0" data-id="element-9634" data-mp="kyq63libh1dr">
                            {f.levelCode}
                          </span>
                        </SelectableRow>;
              })}
                  </div>}
              </div>;
        })}
          {filteredFamilies.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-9635" data-mp="pzxdhjgjryjw">
              <div className="mb-3 flex justify-center" data-id="element-9636" data-mp="288j7xk6100b">
                <DjayNarrator size="md" emotion="thinking" data-id="element-9637" data-mp="26p1s3mrwiwy" />
              </div>
              Geen resultaten voor "{searchQuery}"
            </div>}
        </div>
      </div>;
  };
  // ── Step 3: Students + Deadline + Summary ───────────────────────────────
  const renderStep3 = () => {
    const meta = selectedDomain ? DOMAIN_META[selectedDomain] : null;
    const Icon = meta?.icon;
    return <div className="flex flex-col gap-4 pb-4" data-id="element-9638" data-mp="3fltqh33enrjz">
        <div className="flex items-center gap-2.5" data-id="element-9639" data-mp="3apmua20t9uf8">
          <p className="text-sm font-medium text-slate-600" data-id="element-9640" data-mp="3amqv6ad88zp7">
            Bijna klaar! Kies de leerlingen.
          </p>
        </div>

        {/* Student selection */}
        <div className="grid grid-cols-2 gap-2" data-id="element-9641" data-mp="87ve8qs8tr5q">
          <div onClick={() => setSelectionMode('whole')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'whole' ? 'border-orange-500 bg-orange-50/60' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-9642" data-mp="czwmm1maixqh">
            
            <Users className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'whole' ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-9643" data-mp="3hfxvc5z1y738" />
            
            <div className={`text-sm font-semibold ${selectionMode === 'whole' ? 'text-orange-900' : 'text-slate-700'}`} data-id="element-9644" data-mp="3qf2p6yzo5c92">
              
              Hele klas
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-9645" data-mp="3vbjgg22606qd">
              {students.length} leerlingen
            </div>
          </div>
          <div onClick={() => setSelectionMode('specific')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'specific' ? 'border-orange-500 bg-orange-50/60' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-9646" data-mp="1amvoqrvr4xvr">
            
            <UserCheck className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'specific' ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-9647" data-mp="2lfmnhadvym0h" />
            
            <div className={`text-sm font-semibold ${selectionMode === 'specific' ? 'text-orange-900' : 'text-slate-700'}`} data-id="element-9648" data-mp="2gg1oizypudnr">
              
              Selectie
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-9649" data-mp="27fvb1e30pdol">Kies leerlingen</div>
          </div>
        </div>

        {/* Student list (only when specific) */}
        {selectionMode === 'specific' && <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-9650" data-mp="1xih0tw7otl4q">
            <div className="px-3 py-2 bg-slate-50 text-xs font-bold text-slate-600 border-b border-slate-200" data-id="element-9651" data-mp="poyhd8g290a">
              {selectedStudentIds.length} van {students.length} geselecteerd
            </div>
            <div className="max-h-[200px] overflow-y-auto divide-y divide-slate-100" data-id="element-9652" data-mp="3rjnrmhitlvp">
              {students.map(student => {
            const isSelected = selectedStudentIds.includes(student.id);
            return <SelectableRow key={student.id} selected={isSelected} onToggle={() => toggleStudent(student.id)} label={student.name} className={`flex items-center justify-between px-3 py-2 min-h-[44px] cursor-pointer transition-colors ${isSelected ? 'bg-orange-50/40' : 'hover:bg-slate-50'}`} data-id="element-9653" data-mp="3pyzylvm8hvsw">
                  
                    <div className="flex items-center gap-2.5" data-id="element-9654" data-mp="3lxyaa9tuv45p">
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-9655" data-mp="3780izl0mpyca">
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-slate-800" data-id="element-9656" data-mp="2y72deyajlirl">
                        {student.name}
                      </span>
                    </div>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-9657" data-mp="2jklnaed7fsrn">
                    
                      {isSelected && <Check className="w-3 h-3" data-id="element-9658" data-mp="2smuy75cdxo5k" />}
                    </div>
                  </SelectableRow>;
          })}
            </div>
          </div>}

        {/* Deadline */}
        <div className="flex items-center gap-3" data-id="element-9659" data-mp="32ifx2um5q8b3">
          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-9660" data-mp="32h4swtosuyjg" />
          <div className="flex-1" data-id="element-9661" data-mp="2xjv3he1eumvm">
            <label className="text-xs font-semibold text-slate-500 mb-0.5 block" data-id="element-9662" data-mp="3bbrt9bflwkou">
              Deadline (optioneel)
            </label>
            <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="h-9 text-sm" min={new Date().toISOString().split('T')[0]} data-id="element-9663" data-mp="37b8zvcy2f6kj" />
            
          </div>
        </div>

        {/* Summary card */}
        <Card className="p-3.5 border-orange-200 bg-orange-50/40" data-id="element-9664" data-mp="3gcp8pg98f0mn">
          <div className="flex items-center gap-3" data-id="element-9665" data-mp="2dyho5oql5yqh">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${meta?.bgColor} ${meta?.color}`} data-id="element-9666" data-mp="2hz1kiq3c4ybk">
              
              {Icon && <Icon className="w-5 h-5" data-id="element-9667" data-mp="2mxcxh9oxfoy0" />}
            </div>
            <div className="flex-1 min-w-0" data-id="element-9668" data-mp="2rpef7b4v67vf">
              <div className="font-bold text-slate-900 text-sm" data-id="element-9669" data-mp="2rqfpvehywscm">
                {meta?.label}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-9670" data-mp="2wq25ful9qkol">
                {selectedFamilyIds.length} onderdelen
                <span className="text-slate-300 mx-1.5" data-id="element-9671" data-mp="35omhwe08jy4d">·</span>
                {selectionMode === 'whole' ? `${students.length} leerlingen` : selectionMode === 'specific' ? `${selectedStudentIds.length} leerling(en)` : 'Nog niet gekozen'}
                {deadline && <>
                    <span className="text-slate-300 mx-1.5" data-id="element-9672" data-mp="1bcfwynhji2yv">·</span>
                    tot{' '}
                    {new Date(deadline).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'short'
                })}
                  </>}
              </div>
            </div>
            <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${!isNextDisabled() ? 'text-emerald-500' : 'text-slate-300'}`} data-id="element-9673" data-mp="13xdowhaezyou" />
            
          </div>
        </Card>
      </div>;
  };
  return <SlideOver open={open} onClose={onClose} title="Toets Klaarzetten" description={<span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-orange-100 text-orange-700" data-id="element-9675" data-mp="1rnq50fftd1qi">
          Groep {selectedGroup}
        </span>} width="lg" data-id="element-9674" data-mp="u33t0pm9ki9d">
      
      <div className="flex flex-col h-full" data-id="element-9676" data-mp="1hptpzcjr9mn4">
        {showSuccess ? <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-9677" data-mp="1dpjoac2zxie2">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          bounce: 0.5
        }} className="mb-6" data-id="element-9678" data-mp="6bq1umzumspz">
            
              <DjayNarrator size="lg" emotion="proud" data-id="element-9679" data-mp="21dbbuzgkv42k" />
            </motion.div>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.1
        }} className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4" data-id="element-9680" data-mp="26f0lqbs2dbp2">
            
              <CheckCircle2 className="w-8 h-8" data-id="element-9681" data-mp="2l454pv6onjy8" />
            </motion.div>
            <motion.h2 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-2xl font-black text-slate-900 mb-2" data-id="element-9682" data-mp="2p4p3122zstly">
            
              Toets klaargezet! 🎉
            </motion.h2>
            <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-slate-600 mb-6 max-w-sm" data-id="element-9683" data-mp="340ojb009a026">
            
              De toets{' '}
              <span className="font-bold" data-id="element-9684" data-mp="3e5whi2ibnieb">
                {selectedDomain ? DOMAIN_META[selectedDomain].label : ''}
              </span>{' '}
              voor{' '}
              <span className="font-bold" data-id="element-9685" data-mp="3rz5o5vevxpt5">
                {selectionMode === 'whole' ? 'de hele klas' : `${selectedStudentIds.length} leerling(en)`}
              </span>{' '}
              is klaargezet.
              {deadline && <>
                  {' '}
                  Deadline:{' '}
                  <span className="font-bold" data-id="element-9686" data-mp="2v8vdajwldmbf">
                    {new Date(deadline).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'long'
              })}
                  </span>
                  .
                </>}
            </motion.p>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-8 max-w-sm w-full" data-id="element-9687" data-mp="oerj61v39y5c">
            
              <p className="text-sm text-slate-500" data-id="element-9688" data-mp="23i23ny8dfscx">
                Je kunt deze toets aanpassen of verwijderen op de
                Toetsen-pagina.
              </p>
            </motion.div>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.5
        }} data-id="element-9689" data-mp="1uj6vwhpoia7i">
            
              <Button onClick={() => {
            onSuccess();
            onClose();
          }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-11 font-semibold" data-id="element-9690" data-mp="2n8423mgm8gjw">
              
                Sluiten
              </Button>
            </motion.div>
          </div> : <>
            {/* Step indicator */}
            {renderStepIndicator()}

            {/* Step content with animation */}
            <div className="flex-1 overflow-hidden relative" data-id="element-9691" data-mp="3643yd9gmm69a">
              <AnimatePresence mode="wait" custom={direction} data-id="element-9692" data-mp="3dhcuplya704g">
                <motion.div key={step} custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="absolute inset-0 overflow-y-auto px-0.5" data-id="element-9693" data-mp="3dj659cc0m4hq">
                
                  {step === 1 && renderStep1()}
                  {step === 2 && renderStep2()}
                  {step === 3 && renderStep3()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Running summary + actions */}
            <div className="flex-shrink-0 bg-white" data-id="element-9694" data-mp="a15ujpf2rl0f">
              {renderSummaryStrip()}

              <div className="flex items-center justify-between pt-3 pb-safe" data-id="element-9695" data-mp="ey5zty43ojqh">
                <Button variant="ghost" onClick={step === 1 ? onClose : handleBack} className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-11 px-4" data-id="element-9696" data-mp="j1b1xzk9qjel">
                
                  {step === 1 ? 'Annuleren' : 'Terug'}
                </Button>

                {/* De foutmelding staat naast de knop die hem veroorzaakte,
                   niet bovenaan het paneel: op stap 3 is de onderrand in
                   beeld en de bovenkant weggescrold. */}
                {saveError && <p role="alert" className="flex-1 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mr-3" data-id="element-9697" data-mp="2p23pa8gq0d8d">
                
                    {saveError}
                  </p>}

                {step < 3 ? <Button onClick={handleNext} disabled={isNextDisabled()} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-9698" data-mp="2ysg4dl5rne74">
                
                    Volgende
                    <ChevronRight className="w-4 h-4 ml-1" data-id="element-9699" data-mp="37sd026qkpdbo" />
                  </Button> : <Button onClick={handleConfirm} disabled={isSubmitting || isNextDisabled()} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-9700" data-mp="1idok5virxd8w">
                
                    {isSubmitting ? 'Bezig...' : 'Klaarzetten'}
                  </Button>}
              </div>
            </div>
          </>}
      </div>
    </SlideOver>;
}